/** D5 — PCG32. Named independent streams, all derived from the root seed.
 *
 * State is plain data (I6): two 32-bit halves per 64-bit register, so the whole
 * thing round-trips through JSON without BigInt.
 */

export interface RngStreamState {
  /** 64-bit LCG state, high/low 32-bit halves. */
  readonly hi: number;
  readonly lo: number;
  /** 64-bit stream increment (must be odd), high/low halves. */
  readonly incHi: number;
  readonly incLo: number;
}

const MASK = 0xffffffff;

function mul64(aHi: number, aLo: number, bHi: number, bLo: number): [number, number] {
  const a0 = aLo & 0xffff, a1 = aLo >>> 16, a2 = aHi & 0xffff, a3 = aHi >>> 16;
  const b0 = bLo & 0xffff, b1 = bLo >>> 16, b2 = bHi & 0xffff, b3 = bHi >>> 16;
  let c0 = a0 * b0;
  let c1 = (c0 >>> 16) + a0 * b1;
  c0 &= 0xffff;
  let c2 = c1 >>> 16;
  c1 = (c1 & 0xffff) + a1 * b0;
  c2 += c1 >>> 16;
  c1 &= 0xffff;
  let c3 = c2 >>> 16;
  c2 = (c2 & 0xffff) + a0 * b2 + a1 * b1 + a2 * b0;
  c3 += c2 >>> 16;
  c2 &= 0xffff;
  c3 = (c3 + a0 * b3 + a1 * b2 + a2 * b1 + a3 * b0) & 0xffff;
  return [((c3 << 16) | c2) >>> 0, ((c1 << 16) | c0) >>> 0];
}

function add64(aHi: number, aLo: number, bHi: number, bLo: number): [number, number] {
  const lo = (aLo >>> 0) + (bLo >>> 0);
  const carry = lo > MASK ? 1 : 0;
  return [(((aHi >>> 0) + (bHi >>> 0) + carry) & MASK) >>> 0, (lo & MASK) >>> 0];
}

// PCG32 multiplier 6364136223846793005
const MUL_HI = 0x5851f42d, MUL_LO = 0x4c957f2d;

/** One step. Returns the next state and the 32-bit output of the OLD state.
 *
 * PCG32 XSH-RR: `xorshifted = ((old >> 18) ^ old) >> 27`, `rot = old >> 59`,
 * output = `rotr32(xorshifted, rot)`. Computed on 32-bit halves so no BigInt
 * enters state (I6).
 */
export function next(s: RngStreamState): [RngStreamState, number] {
  const oldHi = s.hi >>> 0, oldLo = s.lo >>> 0;

  const [mHi, mLo] = mul64(oldHi, oldLo, MUL_HI, MUL_LO);
  const [nHi, nLo] = add64(mHi, mLo, s.incHi, s.incLo);

  // (old >> 18) ^ old, as a 64-bit pair
  const sh18Lo = ((oldLo >>> 18) | (oldHi << 14)) >>> 0;
  const sh18Hi = oldHi >>> 18;
  const xLo = (sh18Lo ^ oldLo) >>> 0;
  const xHi = (sh18Hi ^ oldHi) >>> 0;

  // ... >> 27, keeping the low 32 bits
  const xorshifted = (((xLo >>> 27) | (xHi << 5)) >>> 0);

  const rot = oldHi >>> 27;
  const out = rot === 0
    ? xorshifted
    : (((xorshifted >>> rot) | (xorshifted << (32 - rot))) >>> 0);

  return [{ hi: nHi, lo: nLo, incHi: s.incHi, incLo: s.incLo }, out];
}

/** Uniform integer in [0, bound). Rejection-sampled, so the distribution is exact. */
export function nextBounded(s: RngStreamState, bound: number): [RngStreamState, number] {
  if (bound <= 0) return [s, 0];
  const threshold = ((-bound >>> 0) % bound) >>> 0;
  let st = s;
  for (;;) {
    const [n, r] = next(st);
    st = n;
    if (r >= threshold) return [st, r % bound];
  }
}

/** Inclusive integer range. */
export function nextRange(s: RngStreamState, lo: number, hi: number): [RngStreamState, number] {
  const [st, v] = nextBounded(s, hi - lo + 1);
  return [st, lo + v];
}

/** True with probability `bp` basis points (D4: probabilities in basis points). */
export function nextChanceBp(s: RngStreamState, bp: number): [RngStreamState, boolean] {
  const [st, v] = nextBounded(s, 10000);
  return [st, v < bp];
}

function hash32(x: number): number {
  let h = x >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x846ca68b) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}

function strHash(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Seed a named stream from the root seed. Distinct names never share a sequence. */
export function seedStream(rootSeed: number, name: string): RngStreamState {
  const n = strHash(name);
  const incLo = (hash32(rootSeed ^ n) | 1) >>> 0;
  const incHi = hash32(n ^ 0x9e3779b9) >>> 0;
  let s: RngStreamState = { hi: 0, lo: 0, incHi, incLo };
  [s] = next(s);
  const [h2, l2] = add64(s.hi, s.lo, hash32(rootSeed), hash32(rootSeed ^ 0x51ed2701));
  s = { hi: h2, lo: l2, incHi, incLo };
  [s] = next(s);
  return s;
}
